import { useEffect, useState } from 'react';
import { useAdmin } from '@/hooks/useAdmin';
import { Link } from 'react-router-dom';
import { AlertTriangle, ImageOff, FileText, RotateCw, Trash2, Eye, Loader2, Filter } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REASON_ICONS = {
  social_post_leaked_into_body: AlertTriangle,
  empty_body: FileText,
  no_cover: ImageOff,
  flagged: AlertTriangle,
  broken_image: ImageOff,
};

const REASON_COLORS = {
  social_post_leaked_into_body: 'text-amber-400 bg-amber-500/10 border-amber-500/30',
  empty_body: 'text-rose-400 bg-rose-500/10 border-rose-500/30',
  no_cover: 'text-sky-400 bg-sky-500/10 border-sky-500/30',
  flagged: 'text-zinc-300 bg-zinc-500/10 border-zinc-500/30',
};

export default function AdminReview() {
  const { authFetch } = useAdmin();
  const [items, setItems] = useState([]);
  const [byReason, setByReason] = useState({});
  const [reasonFilter, setReasonFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [cleanupBusy, setCleanupBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await authFetch(`${API}/admin/review/needs-review`);
      const d = await r.json();
      setItems(d.items || []);
      setByReason(d.by_reason || {});
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const filtered = reasonFilter === 'all'
    ? items
    : items.filter(i => (i.reason_code || 'flagged') === reasonFilter);

  async function reimport(id) {
    setBusyId(id);
    try {
      const r = await authFetch(`${API}/admin/articles/${id}/reimport-from-source`, { method: 'POST' });
      if (r.ok) { toast.success('Ponovno uvezeno'); load(); }
      else { const e = await r.json().catch(() => ({})); toast.error(e.detail || 'Greška pri uvozu'); }
    } finally { setBusyId(null); }
  }

  async function dismiss(id) {
    setBusyId(id);
    try {
      const r = await authFetch(`${API}/admin/review/articles/${id}/dismiss`, { method: 'POST' });
      if (r.ok) { toast.success('Označeno kao pregledano'); load(); }
    } finally { setBusyId(null); }
  }

  async function del(id) {
    if (!window.confirm('Obrisati članak trajno?')) return;
    setBusyId(id);
    try {
      const r = await authFetch(`${API}/admin/articles/${id}`, { method: 'DELETE' });
      if (r.ok) { toast.success('Obrisano'); load(); }
    } finally { setBusyId(null); }
  }

  async function runCleanup(dryRun) {
    setCleanupBusy(true);
    try {
      const r = await authFetch(`${API}/admin/articles/cleanup-social-bleeds?dry_run=${dryRun ? 'true' : 'false'}`,
        { method: 'POST' });
      if (!r.ok) { toast.error('Greška'); return; }
      const d = await r.json();
      if (dryRun) {
        toast.message(`Pronađeno ${d.count} članaka sa zalivenim social postom — kliknite "Pokreni čišćenje" za stvarno čišćenje.`);
      } else {
        toast.success(`Očišćeno ${d.fixed} članaka.`);
        load();
      }
    } finally { setCleanupBusy(false); }
  }

  const reasonChips = [
    { code: 'all', label: `Sve (${items.length})` },
    ...Object.entries(byReason).map(([k, v]) => ({ code: k, label: `${reasonLabel(k)} (${v})` })),
  ];

  return (
    <div className="p-4 md:p-6" data-testid="admin-review">
      <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
        <div className="flex items-center gap-3">
          <AlertTriangle className="text-amber-500" size={28} />
          <div>
            <h1 className="text-2xl font-bold">Članci za pregled</h1>
            <p className="text-sm text-zinc-400">Sve članke koje algoritam označava kao sumnjive (curenje društvenog posta, prazno tijelo, bez slike).</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => runCleanup(true)} disabled={cleanupBusy}
                  data-testid="dry-run-cleanup-btn"
                  className="px-3 py-2 rounded text-xs font-bold border border-zinc-700 hover:border-amber-500/60 disabled:opacity-50">
            Suho probavanje
          </button>
          <button onClick={() => runCleanup(false)} disabled={cleanupBusy}
                  data-testid="run-cleanup-btn"
                  className="px-3 py-2 rounded text-xs font-bold bg-amber-600 hover:bg-amber-700 text-white disabled:opacity-50">
            {cleanupBusy ? <Loader2 size={12} className="animate-spin"/> : 'Očisti zalivene društvene postove'}
          </button>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <Filter size={14} className="text-zinc-500" />
        {reasonChips.map(c => (
          <button key={c.code} onClick={() => setReasonFilter(c.code)}
                  data-testid={`filter-${c.code}`}
                  className={`px-3 py-1.5 text-xs rounded-full border ${reasonFilter === c.code
                    ? 'border-amber-500 bg-amber-500/10 text-amber-300'
                    : 'border-zinc-700 text-zinc-400 hover:border-zinc-500'}`}>
            {c.label}
          </button>
        ))}
      </div>

      {loading ? <Loader2 className="animate-spin text-zinc-500" /> : (
        <div className="space-y-2">
          {filtered.length === 0 && (
            <div className="p-10 text-center text-zinc-500 border border-dashed border-zinc-800 rounded-xl">
              Nema članaka za pregled. Ovaj prikaz je u redu.
            </div>
          )}
          {filtered.map(it => {
            const Icon = REASON_ICONS[it.reason_code] || AlertTriangle;
            const colorCls = REASON_COLORS[it.reason_code] || REASON_COLORS.flagged;
            return (
              <div key={it.id} data-testid={`review-row-${it.id}`}
                   className="flex items-center gap-3 p-3 rounded-lg border border-zinc-800 bg-zinc-900/50 hover:border-zinc-700">
                <div className={`p-2 rounded ${colorCls}`}><Icon size={16} /></div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm text-white truncate">{it.title_hr || '(bez naslova)'}</p>
                  <p className="text-xs text-zinc-500 mt-0.5">
                    {it.reason_label} · {it.body_length} znakova ·
                    <span className="ml-1 px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-300">{it.status}</span>
                  </p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Link to={`/admin/articles/${it.id}/preview`} target="_blank"
                        className="p-2 rounded bg-zinc-800 hover:bg-zinc-700 text-emerald-400"
                        data-testid={`open-${it.id}`} title="Otvori">
                    <Eye size={14}/>
                  </Link>
                  <button onClick={() => reimport(it.id)} disabled={busyId === it.id}
                          className="p-2 rounded bg-blue-700 hover:bg-blue-600 text-white disabled:opacity-50"
                          data-testid={`reimport-${it.id}`} title="Ponovno uvezi sa izvora">
                    {busyId === it.id ? <Loader2 size={14} className="animate-spin"/> : <RotateCw size={14}/>}
                  </button>
                  <button onClick={() => dismiss(it.id)} disabled={busyId === it.id}
                          className="px-2 py-2 rounded bg-emerald-700 hover:bg-emerald-600 text-white text-xs font-bold disabled:opacity-50"
                          data-testid={`dismiss-${it.id}`} title="Označi pregledano">
                    OK
                  </button>
                  <button onClick={() => del(it.id)} disabled={busyId === it.id}
                          className="p-2 rounded bg-red-800 hover:bg-red-700 text-white disabled:opacity-50"
                          data-testid={`delete-${it.id}`} title="Obriši">
                    <Trash2 size={14}/>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function reasonLabel(code) {
  return ({
    social_post_leaked_into_body: 'Zalijevanje društvenog posta',
    empty_body: 'Prazno tijelo',
    no_cover: 'Bez naslovne slike',
    broken_image: 'Razbijena slika',
    flagged: 'Označeno za pregled',
  })[code] || code;
}
